"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosHeaderButtonsSchema = exports.DEFAULT_POS_HEADER_BUTTONS = exports.APosHeaderButtonSchema = exports.APosHeaderButtonIdSchema = exports.POS_HEADER_BUTTON_IDS = void 0;
exports.normalizeHeaderButtons = normalizeHeaderButtons;
const zod_1 = require("zod");
/**
 * POS Header buttons — the fixed icon buttons beside the sale-screen search box
 * (Scan / enter barcode, Hold, Open orders).
 *
 * Stored additively on a register's `buttonLayout.headerButtons` and on a layout
 * template's `layout.headerButtons`; published from template to register and
 * pushed to tills in the `config:button-layout` payload, exactly like
 * `pinned` / `keypadSlots` / `buttonCustomizations`.
 *
 * Absent, null or [] means "today's look": all three buttons, in the default
 * order, active, no overrides. Array order IS display order (left → right).
 *
 * Ids line up with the POS button ids so the existing gates apply unchanged:
 *   'hold' / 'open-order' — same ids as the rail/keypad buttons (role access,
 *   executeWithApproval). 'scan' — new; opens the Enter-barcode popup.
 *
 * The override fields are the same shape the Side Rail / Keypad buttons store
 * in `buttonCustomizations[id]`, kept per entry here so the header can be styled
 * independently of a Hold key that is still on the keypad.
 */
exports.POS_HEADER_BUTTON_IDS = ['scan', 'hold', 'open-order'];
exports.APosHeaderButtonIdSchema = zod_1.z.enum(exports.POS_HEADER_BUTTON_IDS);
exports.APosHeaderButtonSchema = zod_1.z.object({
    id: exports.APosHeaderButtonIdSchema,
    /** Accessible name / tooltip (the header tiles are icon-only). */
    label: zod_1.z.string().max(24).optional(),
    /** Lucide icon name (PascalCase or kebab-case). Unset = the POS default glyph. */
    icon: zod_1.z.string().max(64).optional(),
    /** Colours — unset = theme default (the search-field surface), as today. */
    bgColor: zod_1.z.string().max(32).optional(),
    textColor: zod_1.z.string().max(32).optional(),
    iconColor: zod_1.z.string().max(32).optional(),
    bgType: zod_1.z.enum(['solid', 'gradient']).optional(),
    gradientFrom: zod_1.z.string().max(32).optional(),
    gradientTo: zod_1.z.string().max(32).optional(),
    gradientAngle: zod_1.z.number().min(0).max(360).optional(),
    /** Needs a manager PIN before the action runs. Default false. */
    requiresApproval: zod_1.z.boolean().optional(),
    /** Who sees the button. Default 'all'. */
    visibility: zod_1.z.enum(['all', 'admin', 'employee']).optional(),
    /** false = hidden on the till. Default true. */
    active: zod_1.z.boolean().optional(),
});
/** Defaults = today's sale-screen header. */
exports.DEFAULT_POS_HEADER_BUTTONS = exports.POS_HEADER_BUTTON_IDS.map((id) => ({ id }));
exports.APosHeaderButtonsSchema = zod_1.z
    .array(exports.APosHeaderButtonSchema)
    .max(exports.POS_HEADER_BUTTON_IDS.length)
    .optional()
    .nullable()
    .transform((v) => normalizeHeaderButtons(v));
/**
 * Normalise whatever is stored into exactly one entry per known id: saved
 * entries first in saved order (unknown ids and duplicates dropped), then any
 * missing ids appended in default order with no overrides. Never throws.
 */
function normalizeHeaderButtons(input) {
    const out = [];
    const seen = new Set();
    if (Array.isArray(input)) {
        for (const raw of input) {
            const parsed = exports.APosHeaderButtonSchema.safeParse(raw);
            if (!parsed.success || seen.has(parsed.data.id))
                continue;
            seen.add(parsed.data.id);
            out.push(parsed.data);
        }
    }
    for (const id of exports.POS_HEADER_BUTTON_IDS) {
        if (!seen.has(id))
            out.push({ id });
    }
    return out;
}
//# sourceMappingURL=pos-header-buttons.schema.js.map